// Center viewport: canvas with integer-snap zoom and panning. Drives a
// pipeline render whenever state changes.

import { effect } from '@/core/signals';
import { state } from '@/core/state';
import { type Pipeline } from '@/core/pipeline';
import { drawToCanvas, ZOOM_LEVELS, snapZoom } from '@/core/render';
import { el, on } from '@/lib/dom';

export type ViewportApi = {
  setZoom(z: number): void;
  zoomIn(): void;
  zoomOut(): void;
  fit(): void;
  getZoom(): number;
  dispose(): void;
};

export type CursorInfo = { x: number; y: number; hex: string } | null;

export function mountViewport(
  host: HTMLElement,
  pipeline: Pipeline,
  onStats?: (text: string) => void,
  onCursor?: (info: CursorInfo) => void,
): ViewportApi {
  const stage = el('div', { class: 'vp-stage' });
  const canvas = el('canvas', { class: 'vp-canvas' }) as HTMLCanvasElement;
  stage.appendChild(canvas);
  host.appendChild(stage);

  let zoom = 4;
  let panX = 0;
  let panY = 0;
  let panning = false;
  let panStart: { x: number; y: number; px: number; py: number } | null = null;
  let inFlight = false;
  let lastImage: ImageData | null = null;

  function repaint(image: ImageData | null): void {
    lastImage = image;
    const w = state.peek().resolution.w;
    const h = state.peek().resolution.h;
    canvas.width = w * zoom;
    canvas.height = h * zoom;
    canvas.style.transform = `translate(${panX}px, ${panY}px)`;
    drawToCanvas(canvas, image, { zoom, offsetX: 0, offsetY: 0 });
  }

  function fit(): void {
    const sw = stage.clientWidth - 24;
    const sh = stage.clientHeight - 24;
    const w = state.peek().resolution.w;
    const h = state.peek().resolution.h;
    if (sw <= 0 || sh <= 0) return;
    const target = Math.min(sw / w, sh / h);
    // Snap to nearest zoom level — gives a more "fills the screen" feel than
    // strictly flooring. The stage hides any tiny overflow.
    zoom = snapZoom(target);
    panX = 0;
    panY = 0;
    triggerRender();
  }

  function setZoom(next: number): void {
    zoom = clamp(next, ZOOM_LEVELS[0] as number, ZOOM_LEVELS[ZOOM_LEVELS.length - 1] as number);
    triggerRender();
  }

  function zoomStep(dir: 1 | -1): void {
    const idx = ZOOM_LEVELS.findIndex((z) => z === zoom);
    if (idx >= 0) {
      const next = ZOOM_LEVELS[clamp(idx + dir, 0, ZOOM_LEVELS.length - 1)] as number;
      setZoom(next);
    } else {
      setZoom(snapZoom(zoom));
    }
  }

  // Wheel zoom (ctrl/cmd) and pan (otherwise)
  on(stage, 'wheel', (ev) => {
    const e = ev as WheelEvent;
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      zoomStep(e.deltaY < 0 ? 1 : -1);
    } else {
      panX -= e.deltaX;
      panY -= e.deltaY;
      canvas.style.transform = `translate(${panX}px, ${panY}px)`;
    }
  }, { passive: false });

  // Space-drag pan
  let spaceHeld = false;
  on(window, 'keydown', (ev) => {
    if (ev.key === ' ' && !ev.repeat) {
      spaceHeld = true;
      stage.classList.add('panning');
    }
  });
  on(window, 'keyup', (ev) => {
    if (ev.key === ' ') {
      spaceHeld = false;
      stage.classList.remove('panning');
    }
  });
  on(stage, 'pointerdown', (ev) => {
    if (!spaceHeld && ev.button !== 1) return;
    panning = true;
    panStart = { x: ev.clientX, y: ev.clientY, px: panX, py: panY };
    stage.setPointerCapture(ev.pointerId);
  });
  on(stage, 'pointermove', (ev) => {
    if (!panning || !panStart) return;
    panX = panStart.px + (ev.clientX - panStart.x);
    panY = panStart.py + (ev.clientY - panStart.y);
    canvas.style.transform = `translate(${panX}px, ${panY}px)`;
  });
  on(stage, 'pointerup', (ev) => {
    if (!panning) return;
    panning = false;
    panStart = null;
    stage.releasePointerCapture(ev.pointerId);
  });

  // Pixel cursor + hex readout
  on(canvas, 'pointermove', (ev) => {
    if (!lastImage || !onCursor) return;
    const rect = canvas.getBoundingClientRect();
    const cx = Math.floor((ev.clientX - rect.left) / zoom);
    const cy = Math.floor((ev.clientY - rect.top) / zoom);
    if (cx < 0 || cy < 0 || cx >= lastImage.width || cy >= lastImage.height) {
      onCursor(null);
      return;
    }
    const i = (cy * lastImage.width + cx) * 4;
    const data = lastImage.data;
    const hex =
      '#' +
      [data[i] as number, data[i + 1] as number, data[i + 2] as number]
        .map((c) => c.toString(16).padStart(2, '0'))
        .join('');
    onCursor({ x: cx, y: cy, hex });
  });
  on(canvas, 'pointerleave', () => {
    onCursor?.(null);
  });

  // Reactive render: re-run pipeline whenever state changes, coalescing.
  let needRerun = false;
  function triggerRender(): void {
    if (inFlight) {
      needRerun = true;
      return;
    }
    inFlight = true;
    needRerun = false;
    void (async () => {
      try {
        const snapshot = state.peek();
        pipeline.prune(snapshot.layers.map((l) => l.id));
        const result = await pipeline.render(snapshot);
        repaint(result.image);
        onStats?.(
          `${snapshot.resolution.w}×${snapshot.resolution.h}  ·  ${zoom}×  ·  ${result.stats.layersRun} run / ${result.stats.layersCached} cached  ·  ${result.stats.ms.toFixed(1)} ms`,
        );
      } finally {
        inFlight = false;
        if (needRerun) triggerRender();
      }
    })();
  }

  const dispose = effect(() => {
    void state.get();
    triggerRender();
  });

  // Fit on first mount once stage layout settles (clientWidth > 0).
  let didInitialFit = false;
  function tryInitialFit(): void {
    if (didInitialFit) return;
    if (stage.clientWidth > 50 && stage.clientHeight > 50) {
      didInitialFit = true;
      fit();
    } else {
      requestAnimationFrame(tryInitialFit);
    }
  }
  requestAnimationFrame(tryInitialFit);

  // Resize observer — re-render on size change. (Don't auto-refit; would fight
  // the user's zoom choice.)
  const ro = new ResizeObserver(() => {
    if (!didInitialFit) tryInitialFit();
    else triggerRender();
  });
  ro.observe(stage);

  // External "fit me" requests (e.g. Studio mode toggle).
  const offFit = on(window, 'pixellab:fit', () => fit());

  return {
    setZoom,
    zoomIn: () => zoomStep(1),
    zoomOut: () => zoomStep(-1),
    fit,
    getZoom: () => zoom,
    dispose: () => {
      dispose();
      ro.disconnect();
      offFit();
    },
  };
}

function clamp(n: number, min: number, max: number): number {
  return n < min ? min : n > max ? max : n;
}
